import React, { FC, useState } from 'react';
import { Modal, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { CopyIcon } from 'components/Elements/CustomSVGIcon';
import { fetchApiAuth } from 'core/utils/api';
import { stripHtmlTags } from 'core/utils/helpers';
import { AccessTypeDocument } from 'core/sites/types';
import { AccessTypeModalProps } from '../../types';
import './accessTypeModal.scss';

const AccessTypeModal: FC<AccessTypeModalProps> = ({
    visible,
    onClose,
    isAdmin,
    accessType,
}) => {
    const [isCopyingPassword, setIsCopyingPassword] = useState(false);

    const handleDocumentDownload = async (doc: AccessTypeDocument) => {
        try {
            const response = await fetchApiAuth({
                method: 'GET',
                responseType: 'blob',
                url: doc.url,
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', doc.filename || doc.title || 'document');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            message.error('Failed to download document');
        }
    };

    const handleCopy = (text: string, field: string) => {
        navigator.clipboard.writeText(text).then(() => {
            message.success(`${field} copied to clipboard`);
        });
    };

    const handleCopyPassword = async () => {
        if (!accessType?.id || isCopyingPassword) return;

        setIsCopyingPassword(true);
        try {
            const response = await fetchApiAuth({
                method: 'GET',
                url: `/access-types/${accessType.id}/password`,
                headers: {
                    'X-Confirm-Sensitive-Action': 'true',
                },
            });
            if (response.data?.password) {
                await navigator.clipboard.writeText(response.data.password);
                message.success('Password copied to clipboard');
            } else {
                message.error('Password not available');
            }
        } catch (error) {
            message.error('Failed to copy password');
        } finally {
            setIsCopyingPassword(false);
        }
    };

    if (!accessType) {
        return null;
    }

    return (
        <Modal
            footer={null}
            width={900}
            centered
            open={visible}
            onCancel={onClose}
            closeIcon={<CloseOutlined />}
            className="access-type-modal">
            <div className="access-type-modal-container">
                <h2 className="heading">Access Type</h2>

                <div className="info-row-grid">
                    <div className="info-item">
                        <div className="item-label">Access Type:</div>
                        <div className="item-value">{accessType.name || '-'}</div>
                    </div>
                    <div className="info-item">
                        <div className="item-label">Email:</div>
                        <div className="item-value">{accessType.email || '-'}</div>
                    </div>
                    <div className="info-item">
                        <div className="item-label">Contact Number:</div>
                        <div className="item-value">{accessType.contactNumber || '-'}</div>
                    </div>
                    <div className="info-item">
                        <div className="item-label">Conf Required:</div>
                        <div className="item-value">{accessType.confRequired ? 'Yes' : 'No'}</div>
                    </div>
                </div>

                {isAdmin && (
                    <div className="info-row-grid admin-row">
                        <div className="info-item">
                            <div className="item-label">Web Link:</div>
                            <div className="item-value">
                                {accessType.webLink ? (
                                    <a
                                        href={accessType.webLink}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {accessType.webLink}
                                    </a>
                                ) : '-'}
                            </div>
                        </div>
                        <div className="info-item">
                            <div className="item-label">Form Link:</div>
                            <div className="item-value">
                                {accessType.formLink ? (
                                    <a
                                        href={accessType.formLink}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {accessType.formLink}
                                    </a>
                                ) : '-'}
                            </div>
                        </div>
                        <div className="info-item">
                            <div className="item-label">Username:</div>
                            <div className="item-value item-value-with-copy">
                                {accessType.username || '-'}
                                {accessType.username && (
                                    <button
                                        type="button"
                                        className="copy-icon"
                                        aria-label="Copy username"
                                        onClick={() => handleCopy(accessType.username ?? '', 'Username')}>
                                        <CopyIcon />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="info-item">
                            <div className="item-label">Password:</div>
                            <div className="item-value item-value-with-copy">
                                {accessType.hasPassword ? '••••••••' : '-'}
                                {accessType.hasPassword && (
                                    <button
                                        type="button"
                                        className={`copy-icon ${isCopyingPassword ? 'loading' : ''}`}
                                        aria-label="Copy password"
                                        disabled={isCopyingPassword}
                                        onClick={handleCopyPassword}>
                                        <CopyIcon />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Notes and Documents - sanitized to strip any HTML */}
                {(accessType.notes || (accessType.documents && accessType.documents.length > 0)) && (
                    <div className="notes-section">
                        {accessType.notes && (
                            <div className="notes-block">
                                <div className="notes-label">Notes:</div>
                                <div className="notes-content">{stripHtmlTags(accessType.notes)}</div>
                            </div>
                        )}

                        {(accessType.documents && accessType.documents.length > 0) && (
                            <div className="documents-block">
                                <div className="documents-label">Documents & Guides:</div>
                                <div className="documents-list">
                                    {accessType.documents.map((doc: AccessTypeDocument) => (
                                        <button
                                            type="button"
                                            key={doc.id}
                                            onClick={() => handleDocumentDownload(doc)}
                                            className="document-link">
                                            {doc.title || doc.filename}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default AccessTypeModal;
